import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Flex, Image, useDisclosure } from '@chakra-ui/react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import { useProducts } from '../../bff-api/products/useProducts';
import { EnhancedError } from '../../components/_error/EnhancedError';
import { Layout } from '../../components/layout/Layout';
import { SoftLink } from '../../components/soft-link/SoftLink';
import { PdpProvider, usePDP } from '../../contexts/pdp/usePDP';
import { breakpoints } from '../../theme/foundations/breakpoints';
import { Breadcrumb, Button, Header, Text } from '../../ui-components';
import { useBreadcrumbs } from '../../ui-components/breadcrumbs/useBreadcrumbs';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { GiftCardsSchema } from '../../utils/schema/GiftCardsSchema';
import { GiftCardsFAQSection } from './components/GiftCardsFAQSection/GiftCardsFAQSection';
import {
    GiftCardsBreadcrumbsSkeleton,
    GiftCardsCardContainerSkeleton,
    GiftCardsFAQCardSkeleton,
    GiftCardsTitleSkeleton,
    GiftCardsYouGiveCardSkeleton,
} from './components/GiftCardsSkeleton';
import { GiftCardsYouGiveSection } from './components/GiftCardsYouGiveSection';
import { CheckBalanceModal } from './components/check-balance-modal';
import { convertFAQItemsToSchemaItems, getFAQItems } from './utils/faq';
import { getRootCategory } from './utils/getRootCategory';

const GiftCardBlock = (): ReactElement | null => {
    const physicalGiftCardImageURL = useCustomPreferenceOrDefault(
        CustomPreference.PHYSICAL_GIFT_CARD_IMAGE_URL,
        '',
    );

    const electronicGiftCardImageURL = useCustomPreferenceOrDefault(
        CustomPreference.ELECTRONIC_GIFT_CARD_IMAGE_URL,
        '',
    );
    const { product } = usePDP();
    const isPhysical = product?.c_gcType === 'physical';

    return (
        <Flex
            direction="column"
            gap={4}
            paddingX={{ base: 4, sm: 8, md: 0 }}
            data-testid={isPhysical ? 'gift-card-physical' : 'gift-card-electronic'}
        >
            <Image
                height={{ base: '240px', md: '300px', lg: '320px', xl: '480px' }}
                width="100%"
                src={isPhysical ? physicalGiftCardImageURL : electronicGiftCardImageURL}
                alt={isPhysical ? 'Physical Gift Card' : 'Electronic Gift Card'}
                objectFit="cover"
            />
            <Flex direction="column" gap={6} alignItems="center">
                <Flex direction="column" gap={1} maxWidth={{ xl: '60%' }} alignItems="center">
                    <Header as="h4" variant="header4" align="center" style="bold">
                        {isPhysical ? (
                            <FormattedMessage
                                id="gift_cards.landing_page.physical_git_card.title"
                                defaultMessage="Physical Gift Cards"
                            />
                        ) : (
                            <FormattedMessage
                                id="gift_cards.landing_page.e_git_card.title"
                                defaultMessage="E-Gift Cards"
                            />
                        )}
                    </Header>
                    <Text align="center">
                        {isPhysical ? (
                            <FormattedMessage
                                id="gift_cards.landing_page.physical_git_card.description"
                                defaultMessage="The perfect gift to give and get, Sur La Table gift cards can be shipped or picked up at a store near you."
                            />
                        ) : (
                            <FormattedMessage
                                id="gift_cards.landing_page.e_git_card.description"
                                defaultMessage="Give a gift that every cook would love to find in their inbox. E-gift cards are just as flexible as our physical gift cards and make the perfect last-minute presents."
                            />
                        )}
                    </Text>
                </Flex>
                <Flex justifyContent="center">
                    <SoftLink
                        to={product?.c_pwaUrl ?? ''}
                        data-testid={`${product?.c_gcType}-gift-card-landing-cta`}
                    >
                        <Button
                            variant="primary-black"
                            id={
                                isPhysical
                                    ? 'gift-card-physical-button'
                                    : 'gift-card-electronic-button'
                            }
                        >
                            {isPhysical ? (
                                <FormattedMessage
                                    id="gift_cards.landing_page.physical_git_card.button"
                                    defaultMessage="Mail a Gift Card"
                                />
                            ) : (
                                <FormattedMessage
                                    id="gift_cards.landing_page.e_git_card.button"
                                    defaultMessage="Email a Gift Card"
                                />
                            )}
                        </Button>
                    </SoftLink>
                </Flex>
            </Flex>
        </Flex>
    );
};

const pgcSku = 'GCP-15269';
const egcSku = 'GCP-15271';

export const GiftCardsLayout = (): ReactElement | null => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    const { data, isFetching: isLoading } = useProducts([pgcSku, egcSku]);
    const { physicalCardProduct } = useMemo(
        () => ({
            physicalCardProduct: data?.find((product) => product.id === pgcSku),
        }),
        [data],
    );

    const intl = useIntl();
    const breadcrumbs = useBreadcrumbs(
        getRootCategory(physicalCardProduct?.c_primaryCategory?.parentCategoryTree),
    );

    const faqItems = getFAQItems(intl, onOpen);
    const faqSchemaItems = convertFAQItemsToSchemaItems(faqItems);

    return (
        <Layout>
            <GiftCardsSchema breadcrumbTrail={breadcrumbs} faqItems={faqSchemaItems} />
            <Flex
                direction="column"
                gap={{ base: 8 }}
                paddingBottom={{ base: 8, lg: 16 }}
                paddingX={{ md: 8, lg: 10, xl: 16 }}
                data-testid="gift-cards-layout"
                maxWidth={breakpoints.xl}
                marginX="auto"
            >
                <CheckBalanceModal id="check-balance-modal" isOpen={isOpen} onClose={onClose} />
                <Flex direction="column">
                    <GiftCardsBreadcrumbsSkeleton isLoaded={!isLoading}>
                        <Breadcrumb itemsList={breadcrumbs} />
                    </GiftCardsBreadcrumbsSkeleton>
                    <GiftCardsTitleSkeleton isLoaded={!isLoading}>
                        <Header as="h2" variant="header2" style="bold">
                            <FormattedMessage id="gift_cards.title" defaultMessage="Gift Cards" />
                        </Header>
                    </GiftCardsTitleSkeleton>
                </Flex>
                <Flex gap={{ base: 8, md: 16 }} direction="column">
                    {data?.length || isLoading ? (
                        <Flex
                            direction={{ base: 'column', md: 'row' }}
                            gap={{ base: 8 }}
                            justifyContent="center"
                            data-testid="gift-cards-layout-cards"
                        >
                            {isLoading ? (
                                <>
                                    <GiftCardsCardContainerSkeleton />
                                    <GiftCardsCardContainerSkeleton />
                                </>
                            ) : (
                                data?.map((product) => (
                                    <Flex key={product.id} flex={1} maxWidth="820px">
                                        <PdpProvider
                                            productId={product?.variants?.[0].productId || ''}
                                        >
                                            <GiftCardBlock />
                                        </PdpProvider>
                                    </Flex>
                                ))
                            )}
                        </Flex>
                    ) : null}
                    <GiftCardsYouGiveCardSkeleton isLoaded={!isLoading}>
                        <GiftCardsYouGiveSection onCheckBalanceClick={onOpen} />
                    </GiftCardsYouGiveCardSkeleton>
                    <GiftCardsFAQCardSkeleton isLoaded={!isLoading}>
                        <GiftCardsFAQSection onCheckBalanceClick={onOpen} />
                    </GiftCardsFAQCardSkeleton>
                </Flex>
            </Flex>
        </Layout>
    );
};

const GiftCards: PageFC = () => (
    <ErrorBoundary FallbackComponent={EnhancedError}>
        <GiftCardsLayout />
    </ErrorBoundary>
);

export { GiftCards };
